/**
 * Portal Auth — one clinic's drill-down, the `Portal Auth` tab on the clinic
 * detail page.
 *
 * TEMPORARY MIGRATION TOOLING - TO BE REMOVED AFTER CUTOVER.
 *
 * A tab inside `ClinicDetailView` rather than a page of its own, because here
 * it IS one more facet of a clinic already on screen — the opposite of the
 * fleet view, which is its own nav item because it answers a different question
 * and is checked daily.
 *
 * Counts, plus the events behind them. No identity search, no unlock, no
 * send-link, no session revoke — those belong in odin, where support already
 * has the patient in front of them. A clickable stat opens
 * `PortalAuthEventsPanel`: one row per event with its reason and a link to the
 * patient in odin, never an address. Nothing here mutates anything.
 *
 * WHAT THIS SHOWS THAT NOTHING ELSE DOES. Three of these numbers exist nowhere
 * else in the system:
 *   - failed migrations: a patient whose Firebase password Google verified
 *     and who still did not move. `write_failed` among them is a patient who
 *     was signed in and so never noticed.
 *   - `below_policy` — migrated passwords shorter than the 12-character rule,
 *     whose owners the portal is asking to pick a stronger one.
 *   - duplicate-email patients surface today one at a time, as a patient who
 *     cannot sign in (`identity_sync.py` writes no row).
 */

import { useCallback, useEffect, useState } from 'react'
import { RefreshCw, Loader2 } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent, Button, Badge } from '../ui'
import { portalAuthAPI } from '../../services/api'
import PortalAuthEventsPanel from './PortalAuthEventsPanel'

function apiErrorMessage(err, fallback) {
  return err?.response?.data?.error?.message || fallback
}

function num(value) {
  return Number.isFinite(value) ? value.toLocaleString() : '—'
}

/**
 * One labelled figure, with the sentence that says what it means. With
 * `onOpen` it is a button that opens the events behind the number.
 */
function Stat({ label, value, hint, tone, onOpen }) {
  const toneClass =
    tone === 'bad' && Number(value) > 0
      ? 'text-destructive'
      : tone === 'warn' && Number(value) > 0
        ? 'text-orange-600'
        : ''
  const body = (
    <>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-xl font-semibold tabular-nums ${toneClass} ${onOpen ? 'underline decoration-dotted underline-offset-4' : ''}`}>
        {num(value)}
      </p>
      {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
    </>
  )
  if (!onOpen) return <div>{body}</div>
  return (
    <button
      type="button"
      onClick={onOpen}
      className="text-left rounded-md -m-2 p-2 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
      title="Show these events"
    >
      {body}
    </button>
  )
}

/**
 * A funnel: three counts and no subtraction between them.
 *
 * `requested − sent` and `sent − consumed` over one window are not facts about
 * any link — one requested on day 8 and used on day 7 breaks both — so neither
 * is shown. "Never sent" is the count of send-failure events for this funnel's
 * purpose (missing email config, Postmark rejected, an exception), and clicking
 * it lists them, reason by reason.
 */
function Funnel({ title, funnel, onOpenNeverSent }) {
  if (!funnel) return null
  const neverSent = Number(funnel.neverSent) || 0
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{title}</p>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge variant="outline">Requested {num(funnel.requested)}</Badge>
        <span className="text-xs text-muted-foreground">→</span>
        <Badge variant="outline">Sent {num(funnel.sent)}</Badge>
        <span className="text-xs text-muted-foreground">→</span>
        <Badge variant="outline">Consumed {num(funnel.consumed)}</Badge>
      </div>
      <button
        type="button"
        onClick={onOpenNeverSent}
        className={`text-xs underline decoration-dotted underline-offset-4 ${neverSent > 0 ? 'text-destructive font-medium' : 'text-muted-foreground'}`}
      >
        {num(funnel.neverSent)} never sent — show send failures
      </button>
    </div>
  )
}

/**
 * `{ total, display }` tallies as a labelled row, zeroes included. With
 * `onOpen` the label opens the events behind it.
 */
function TallyRow({ label, tally, displayDays, windowDays, tone, onOpen }) {
  const value = tally || { total: 0, display: 0 }
  const bad = tone === 'bad' && value.total > 0
  const warn = tone === 'warn' && value.total > 0
  const labelClass = `text-sm text-left ${bad ? 'text-destructive font-medium' : warn ? 'text-orange-600' : ''}`
  return (
    <div className="flex items-baseline justify-between gap-4 py-1 border-b last:border-b-0">
      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          className={`${labelClass} underline decoration-dotted underline-offset-4 hover:text-foreground`}
          title="Show these events"
        >
          {label}
        </button>
      ) : (
        <span className={labelClass}>{label}</span>
      )}
      <span className="text-sm tabular-nums text-muted-foreground">
        <span className={bad ? 'text-destructive font-medium' : warn ? 'text-orange-600' : 'text-foreground'}>{value.display}</span>
        {` in ${displayDays}d · ${value.total} in ${windowDays}d`}
      </span>
    </div>
  )
}

/**
 * `migration_failed` reasons, in the order the card lists them, each with what
 * it means for the patient. Kept in step with `MIGRATION_FAILURE_REASONS` in
 * `portalAuthMetrics.js`.
 */
const MIGRATION_FAILURE_ROWS = [
  ['id_token_rejected', "core couldn't verify Google's token — usually configuration, and usually everyone"],
  ['id_token_stale', 'token too old or from the future — clock skew'],
  ['no_email_in_token', 'Google token carried no email'],
  ['no_identity', 'in Firebase, but no patient record matched the address'],
  ['write_failed', 'signed in, but the hash was not saved — still on Firebase'],
  ['identity_locked', 'core identity locked from earlier attempts; expires in 15 min'],
  ['identity_not_active', 'identity disabled in core'],
]

/** The population toggle: keys of the row's `population`. */
const POPULATIONS = [
  { key: 'all', label: 'All' },
  { key: 'patients', label: 'Patients' },
  { key: 'partial_patient', label: 'Partial Patients' },
]

/** A percentage over its count; "n/a" when the figure does not apply. */
function PercentStat({ label, pct, count, na }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-xl font-semibold tabular-nums">
        {na ? 'n/a' : Number.isFinite(pct) ? `${pct}%` : '—'}
      </p>
      {!na && <p className="text-xs text-muted-foreground mt-0.5 tabular-nums">{num(count)}</p>}
    </div>
  )
}

function percentOf(part, whole) {
  return whole > 0 ? `${((part / whole) * 100).toFixed(1)}%` : null
}

export default function PortalAuthTab({ clinicId }) {
  const [data, setData] = useState(null)
  const [phase, setPhase] = useState('loading')
  const [error, setError] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  // The stat whose events the slide-out panel is showing; null when closed.
  const [openStat, setOpenStat] = useState(null)
  const [population, setPopulation] = useState('all')

  const load = useCallback(async (bypassCache) => {
    if (bypassCache) setRefreshing(true)
    else setPhase('loading')
    setError(null)
    try {
      const response = await portalAuthAPI.getClinic(clinicId, bypassCache)
      setData(response.data.data)
      setPhase('ready')
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to load portal auth detail'))
      setPhase('error')
    } finally {
      setRefreshing(false)
    }
  }, [clinicId])

  useEffect(() => {
    load(false)
  }, [load])

  if (phase === 'loading') {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">
          <Loader2 className="h-5 w-5 inline animate-spin mr-2" /> Loading portal auth…
        </CardContent>
      </Card>
    )
  }

  if (phase === 'error') {
    return (
      <Card>
        <CardContent className="py-6 text-center space-y-3">
          <p className="text-destructive">{error}</p>
          <Button variant="outline" onClick={() => load(false)}>Try Again</Button>
        </CardContent>
      </Card>
    )
  }

  const { row, detail } = data
  const windowDays = detail?.window?.days ?? 14
  const displayDays = detail?.window?.displayDays ?? 7
  const totals = row?.population?.[population]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end gap-2 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => load(true)} disabled={refreshing}>
          {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {(row?.errors || []).length > 0 && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm space-y-1">
          {row.errors.map((entry, index) => (
            <p key={index}>{entry.message}{entry.status ? ` (${entry.status})` : ''}</p>
          ))}
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap space-y-0">
          <CardTitle>Migration</CardTitle>
          <div className="flex flex-wrap gap-2">
            {POPULATIONS.map(({ key, label }) => (
              <Button
                key={key}
                size="sm"
                variant={population === key ? 'default' : 'outline'}
                onClick={() => setPopulation(key)}
              >
                {label}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="grid gap-6 grid-cols-2 md:grid-cols-4">
          <Stat label="Users" value={totals?.users} />
          <PercentStat label="Migrated" pct={totals?.migratedPercent} count={totals?.migrated} />
          <PercentStat
            label="Safe to delete"
            pct={totals?.safeToDeletePercent}
            count={totals?.safeToDelete}
            na={totals ? totals.safeToDelete === null : false}
          />
        </CardContent>
        <CardContent className="pt-0 grid gap-6 grid-cols-2 md:grid-cols-4">
          <Stat
            label="Logins with no core password"
            value={detail?.firstLogins?.noCorePassword?.display}
            onOpen={() => setOpenStat({ type: 'login_no_core_password', title: 'Logins with no core password' })}
          />
          <Stat label="Reached migration" value={detail?.migration?.attempted?.display} />
          <Stat
            label="Unknown accounts"
            value={detail?.firstLogins?.unknownAccount?.display}
            onOpen={() => setOpenStat({ type: 'login_unknown_account', title: 'Unknown accounts' })}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Identity composition</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-6 grid-cols-2 md:grid-cols-4">
          <Stat label="Dripped from Firebase" value={detail?.composition?.dripped} />
          <Stat label="Odin" value={detail?.composition?.selfSet} />
          <Stat label="Below policy" value={detail?.composition?.belowPolicy} tone="warn" />
          <Stat label="Duplicate emails" value={detail?.duplicateEmails} tone="warn" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Backfill trigger mix</CardTitle>
        </CardHeader>
        <CardContent>
          <TallyRow label="Intake" tally={detail?.backfillTriggers?.ongoing} displayDays={displayDays} windowDays={windowDays} />
          <TallyRow label="Sign In Request" tally={detail?.backfillTriggers?.magic_request} displayDays={displayDays} windowDays={windowDays} />
          <TallyRow label="Forgot Password Request" tally={detail?.backfillTriggers?.password_forgot} displayDays={displayDays} windowDays={windowDays} />
          <TallyRow label="Drip Migration" tally={detail?.backfillTriggers?.firebase_migrate} displayDays={displayDays} windowDays={windowDays} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Migration attempts</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <TallyRow label="Attempted" tally={detail?.migration?.attempted} displayDays={displayDays} windowDays={windowDays}
              onOpen={() => setOpenStat({ type: 'migration_attempted', title: 'Migration attempts' })} />
            <TallyRow
              label={`Migrated${percentOf(detail?.migration?.migrated?.display, detail?.migration?.attempted?.display) ? ` — ${percentOf(detail.migration.migrated.display, detail.migration.attempted.display)} of attempts` : ''}`}
              tally={detail?.migration?.migrated} displayDays={displayDays} windowDays={windowDays}
              onOpen={() => setOpenStat({ type: 'password_migrated', title: 'Migrated' })} />
            <TallyRow label="Failed — we failed a patient who was right" tally={detail?.migration?.failed} displayDays={displayDays} windowDays={windowDays} tone="bad"
              onOpen={() => setOpenStat({ type: 'migration_failed', title: 'Migration failed' })} />
            <TallyRow label="No outcome — the request crashed, or a write is still in flight" tally={detail?.migration?.noOutcome} displayDays={displayDays} windowDays={windowDays} tone="warn" />
            <TallyRow label="Already had a core password — old Firebase password typed; not a failure" tally={detail?.migration?.alreadyMigrated} displayDays={displayDays} windowDays={windowDays} />
          </div>
          <div>
            <p className="text-sm font-medium mb-1">Failed by reason</p>
            {MIGRATION_FAILURE_ROWS.map(([reason, meaning]) => (
              <TallyRow
                key={reason}
                label={meaning}
                tally={detail?.migration?.failedByReason?.[reason]}
                displayDays={displayDays}
                windowDays={windowDays}
                tone="bad"
                onOpen={() => setOpenStat({ type: 'migration_failed', reason, title: `Migration failed — ${reason}` })}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Link funnels</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <Funnel
            title="Magic link"
            funnel={detail?.funnels?.magic}
            onOpenNeverSent={() => setOpenStat({ type: 'magic_send_failed', purpose: 'magic_link', title: 'Magic link — never sent' })}
          />
          <Funnel
            title="Password reset"
            funnel={detail?.funnels?.reset}
            onOpenNeverSent={() => setOpenStat({ type: 'magic_send_failed', purpose: 'password_reset', title: 'Password reset — never sent' })}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Failures</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-6 grid-cols-2 md:grid-cols-5">
          <Stat
            label="Login failed"
            value={detail?.failures?.loginFailed}
            onOpen={() => setOpenStat({ type: 'login_failed', title: 'Login failed' })}
          />
          <Stat
            label="Accounts locked"
            value={detail?.failures?.accountLocked}
            tone="warn"
            onOpen={() => setOpenStat({ type: 'account_locked', title: 'Accounts locked' })}
          />
          <Stat
            label="Link send failed"
            value={detail?.failures?.magicSendFailed}
            tone="bad"
            hint="magic link and reset emails"
            onOpen={() => setOpenStat({ type: 'magic_send_failed', title: 'Link send failed' })}
          />
          <Stat
            label="Rate limited"
            value={detail?.failures?.rateLimited}
            onOpen={() => setOpenStat({ type: 'rate_limited', title: 'Rate limited' })}
          />
          <Stat
            label="Nonce mismatch"
            value={detail?.failures?.nonceMismatch}
            tone="warn"
            onOpen={() => setOpenStat({ type: 'nonce_mismatch', title: 'Nonce mismatch' })}
            hint={
              Number.isFinite(detail?.failures?.nonceMismatchRate)
                ? `${detail.failures.nonceMismatchRate}% of magic links consumed`
                : 'no links consumed — no rate to report'
            }
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>State</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-6 grid-cols-2 md:grid-cols-4">
          <Stat label="Disabled identities" value={detail?.state?.disabled} tone="warn" />
          <Stat label="Locked right now" value={detail?.state?.lockedNow} tone="warn" />
          <Stat label="Email verified" value={detail?.state?.emailVerified} />
          <Stat label="Live sessions" value={detail?.state?.liveSessions} />
        </CardContent>
      </Card>

      <PortalAuthEventsPanel clinicId={clinicId} stat={openStat} onClose={() => setOpenStat(null)} />
    </div>
  )
}
